import { Button, Table } from 'antd';
import { BellPlus, SearchX, ShieldAlert } from 'lucide-react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import type { AlertRuleView } from '../alerts.api';
import { useAlertRules } from '../hooks/use-alert-rules';
import { AlertRuleDrawer } from './AlertRuleDrawer';
import { alertRuleColumns } from './alert-rule-columns';

/** The rules that raise alerts: add one, change one in place, switch one off. Rules are never deleted, so the alerts they raised keep their rule. */
export function AlertRulesTab() {
  const rules = useAlertRules();
  const { state, list } = rules;

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'error')
    return <EmptyState icon={ShieldAlert} title="Couldn't load rules" description="Something went wrong loading the alert rules. Try reloading the page." />;
  if (state.status === 'loading') return <TableSkeleton label="Loading alert rules" withSearch />;

  const emptyText = list.isFiltered ? (
    <EmptyState icon={SearchX} title="No matching rules" description="No rules match this search or these filters." />
  ) : (
    <EmptyState icon={BellPlus} title="No alert rules yet" description="Add a rule to be told when a truck goes quiet, crosses a geofence or speeds." />
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ListSearch value={list.query.q} placeholder="Search rule name" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
        <Button type="primary" icon={<BellPlus className="size-4" />} onClick={() => rules.openEditor('new')}>
          Create rule
        </Button>
      </div>
      <Table<AlertRuleView>
        rowKey="id"
        dataSource={state.page.items}
        columns={alertRuleColumns({ list, onEdit: rules.openEditor, onToggle: rules.toggle })}
        loading={rules.isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText }}
        {...list.tableProps<AlertRuleView>(state.page.total)}
      />
      <AlertRuleDrawer
        editor={rules.editor}
        roles={rules.roles}
        error={rules.saveError}
        saving={rules.saving}
        onSave={rules.save}
        onClose={rules.closeEditor}
      />
    </div>
  );
}
